import { Badge, Tooltip } from "@mantine/core";
import { useSessionStorage } from "@mantine/hooks";
import { IconListCheck } from "@tabler/icons-react";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  useMantineReactTable
} from "mantine-react-table-open";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { getMinimalMRTOptions } from "../../../mantine/getMinimalMRTOptions";
import { useStartPageCompanies } from "../hooks/useStartPageCompanies";
import { useStartPageRegulatoryTodos } from "../hooks/useStartPageRegulatoryTodos";
import type { StartPageRegulatoryTodo } from "../StartPage.types";
import { StartPageRegulatoryTodoTag } from "../StartPage.types";
import { Tile } from "./Tile";

interface RegulatoryTodosTileProps {
  projectId?: string;
}

function RegulatoryTodosTile({ projectId }: RegulatoryTodosTileProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [storedRegulatoryCompanyId, setStoredRegulatoryCompanyId] =
    useSessionStorage<number | undefined>({
      key: "startPageRegulatoryCompanyId",
      defaultValue: undefined
    });

  const { companies } = useStartPageCompanies(projectId);
  const {
    data: regulatoryTodos,
    error: regulatoryTodosError,
    isLoading: regulatoryTodosLoading
  } = useStartPageRegulatoryTodos(
    currentPage,
    pageSize,
    projectId,
    storedRegulatoryCompanyId
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [projectId, storedRegulatoryCompanyId]);

  useEffect(() => {
    if (
      companies &&
      storedRegulatoryCompanyId &&
      !companies?.find((company) => company.id === storedRegulatoryCompanyId)
    ) {
      setStoredRegulatoryCompanyId(undefined);
    }
  }, [companies, setStoredRegulatoryCompanyId, storedRegulatoryCompanyId]);

  function handleCompanyChange(newSiteId: number | undefined) {
    setCurrentPage(1);
    setStoredRegulatoryCompanyId(newSiteId);
  }

  function handleSwitchPage(newCurrentPage: number, newPageSize: number) {
    setCurrentPage(newCurrentPage);
    setPageSize(newPageSize);
  }

  const allCompaniesChoice = { value: undefined, label: "Alle Unternehmen" };
  const companiesOptions = companies
    ? [
        allCompaniesChoice,
        ...companies.map((company) => ({
          label: company.name,
          value: company.id
        }))
      ]
    : [];

  const mrtColumns: Array<MRT_ColumnDef<StartPageRegulatoryTodo>> = useMemo(
    () => [
      { header: "Unternehmen", accessorKey: "person_name" },
      {
        header: "Aufgabe",
        accessorKey: "name",
        Cell: ({ row }) => (
          <Link
            to={`../projekte/${row.original.project_id}/aufgaben/${row.original.id}/dokumente`}
          >
            {row.original.name}
          </Link>
        )
      },
      {
        header: "Status",
        accessorKey: "tag",
        size: 160,
        grow: false,
        Cell: ({ row }) => <RegulatoryTodoBadge regulatoryTodo={row.original} />
      }
    ],
    []
  );

  const mrtTable = useMantineReactTable<StartPageRegulatoryTodo>({
    ...getMinimalMRTOptions({
      emptyRowsFallbackText: "Super, alles erledigt!"
    }),
    state: {
      isLoading: regulatoryTodosLoading,
      pagination: { pageIndex: currentPage - 1, pageSize: pageSize }
    },
    mantineTableHeadRowProps: { display: "none" },
    layoutMode: "grid",
    enableBottomToolbar: true,
    enablePagination: true,
    manualPagination: true,
    paginationDisplayMode: "pages",
    autoResetPageIndex: false,
    onPaginationChange: (pagination) => {
      if (typeof pagination === "function") {
        const currentState = { pageIndex: currentPage - 1, pageSize };
        const newState = pagination(currentState);
        handleSwitchPage(newState.pageIndex + 1, newState.pageSize);
      }
    },
    mantinePaginationProps: { size: "xs" },
    rowCount: regulatoryTodos ? regulatoryTodos.count : 0,
    columns: mrtColumns,
    data: regulatoryTodos ? regulatoryTodos.results : []
  });

  return (
    <Tile
      error={regulatoryTodosError}
      filterOptions={companiesOptions}
      filterPlaceholder="Auswahl des Unternehmens"
      filterValue={storedRegulatoryCompanyId}
      icon={IconListCheck}
      title="Anstehende Aufgaben"
      onFilterChange={handleCompanyChange}
    >
      <MantineReactTable table={mrtTable} />
    </Tile>
  );
}

interface RegulatoryTodoTagProps {
  regulatoryTodo: StartPageRegulatoryTodo;
}

function RegulatoryTodoBadge({ regulatoryTodo }: RegulatoryTodoTagProps) {
  if (regulatoryTodo.tag === StartPageRegulatoryTodoTag.DUE) {
    return (
      <Badge color="error" miw={60} size="sm">
        Überfällig
      </Badge>
    );
  } else if (regulatoryTodo.tag === StartPageRegulatoryTodoTag.NEW) {
    return (
      <Badge color="info" miw={60} size="sm">
        Neu
      </Badge>
    );
  } else if (regulatoryTodo.due_date) {
    return (
      <Tooltip label={`Fällig am ${regulatoryTodo.due_date}`}>
        <Badge color="info" miw={60} size="sm">
          Fällig am {regulatoryTodo.due_date}
        </Badge>
      </Tooltip>
    );
  } else {
    return null;
  }
}

export { RegulatoryTodosTile, RegulatoryTodosTileProps };
